import { useSwym } from '@utils/Swym';
import { pubSub } from '@utils/PubSub';

/**
 * @typedef {Object} ProductData
 * @property {number|null} epi - Variant ID
 * @property {number|null} empi - Product ID
 * @property {string|null} du - Product URL
 * @property {string|null} dt - Product title
 * @property {string|null} source - Wishlist source
 * @property {Object|null} cprops - Custom properties
 */

/**
 * @typedef {Object} ElementLabels
 * @property {string} aria - ARIA label text
 * @property {string} title - Title text
 */

/**
 * @typedef {Object} ElementConfig
 * @property {ElementLabels} labels - Labels for inactive state
 * @property {ElementLabels} active - Labels for active state
 */

/**
 * @typedef {Object} ComponentState
 * @property {boolean} isInitialized - Whether the component has been initialized
 * @property {boolean} isInWishlist - Whether the product is in the wishlist
 * @property {boolean} isLoading - An async operation is in progress
 * @property {boolean} error - An error has occurred
 */

/**
 * Custom web component for managing wishlist functionality with Swym.
 * Provides a button for adding/removing products from a wishlist.
 * @extends HTMLElement
 */
export class SwymButton extends HTMLElement {
  /**
   * Defines which attributes should trigger attributeChangedCallback when modified.
   * @returns {string[]} Array of observed attribute names
   */
  static get observedAttributes() {
    return ['epi', 'empi', 'du', 'dt', 'source', 'cprops'];
  }

  /**
   * Creates a new SwymButton instance and initializes its state.
   */
  constructor() {
    super();

    /**
     * Product data object containing Swym-specific properties.
     * @type {ProductData}
     */
    this.product = {
      epi: null,
      empi: null,
      du: null,
      source: null,
      cprops: null,
      dt: null,
    };

    /**
     * Element config for labels and accessibility attributes.
     * @type {ElementConfig}
     */
    this.element = {
      labels: {
        aria: 'Save product to wishlist',
        title: 'Save',
      },
      active: {
        aria: 'Product saved to wishlist',
        title: 'Saved',
      },
    };

    /**
     * Component state tracking initialization, wishlist status, and loading.
     * @type {ComponentState}
     */
    this.state = {
      isInitialized: false,
      isInWishlist: false,
      isLoading: false,
      error: false,
    };

    /**
     * Bound click handler for wishlist updates.
     * @type {Function}
     */
    this.handleClick = this.updateWishlist.bind(this);

    /**
     * Bound handler for Swym service updates.
     * @type {Function}
     */
    this.handleServiceUpdate = this.updateWishlistStatus.bind(this);
  }

  /**
   * Checks if all required product data is present.
   * @returns {boolean} True if epi, empi, du, dt, and cprops are all set
   */
  get hasRequiredData() {
    return (
      this.product.epi &&
      this.product.empi &&
      this.product.du &&
      this.product.dt &&
      this.product.cprops
    );
  }

  /**
   * Gets the current wishlist status of the product.
   * @returns {boolean} True if product is in wishlist
   */
  get isInWishlist() {
    return this.state.isInWishlist;
  }

  /**
   * Gets the selected option from custom properties.
   * @returns {*} The selected option value from cprops
   */
  get selectedOption() {
    return this.product.cprops.selectedOption;
  }

  get requestInProgress() {
    const template = document.querySelector('body');
    return template.classList.contains('wishlist-notification--active');
  }

  /**
   * Returns whether the current interaction is from quickview
   * @returns {Boolean}
   */
  get isQuickview() {
    const attribute = this.getAttribute('quick-view') ?? false;
    try {
      return JSON.parse(attribute) === true;
    } catch {
      return false;
    }
  }

  get isResultsPage() {
    const template = document.querySelector('body');
    return template.classList.contains('wishlist-page');
  }

  /**
   * Updates the wishlist status by checking with the Swym service.
   * Initializes the component on first call and unsubscribes from ready event.
   * @async
   * @returns {Promise<void>}
   */
  async updateWishlistStatus() {
    if (!this.state.isInitialized) {
      this.state.isInitialized = true;
      pubSub.unsubscribe('swym:wishlist:ready', this.handleServiceUpdate);
    }

    if (this.state.isLoading) return;

    this.state.isLoading = true;
    const { empi } = this.product;

    this.state.isInWishlist = (await useSwym.isInWishlist(
      empi,
      this.selectedOption
    ))
      ? true
      : false;
    this.state.isLoading = false;
    this.render();
  }

  /**
   * Toggles the product in/out of the wishlist based on current state.
   * Prevents action if required data is missing or component is loading.
   * @async
   * @param {Event} e - Click event object
   * @returns {Promise<void>}
   * @throws {Error} If wishlist update fails
   */
  async updateWishlist(e) {
    e.preventDefault();

    if (!this.hasRequiredData || this.state.isLoading || this.requestInProgress)
      return;

    const { empi } = this.product;
    let response;
    this.state.isLoading = true;
    const isQuickview = this.isQuickview;
    const isResultsPage = this.isResultsPage;
    const variantPresentInWishList = await useSwym.isInWishlist(
      empi,
      this.selectedOption
    );

    try {
      if (variantPresentInWishList) {
        const payload = {
          empi: variantPresentInWishList.empi,
          epi: variantPresentInWishList.epi,
          du: variantPresentInWishList.du,
          cprops: variantPresentInWishList.cprops,
        };
        response = await useSwym.removeFromWishlist(payload);
        this.state.isInWishlist = false;
        this.notify(response);
        if (isQuickview) this.removeScrollLock();
      } else {
        response = await useSwym.addToWishlist(this.product);
        this.state.isInWishlist = true;
        this.notify(response);
      }
    } catch (error) {
      throw new Error(`Swym failed to update wishlist ${error}`);
    } finally {
      this.state.isLoading = false;
      this.render();
      if (isQuickview || isResultsPage) pubSub.publish('swym:service:update');
    }
  }

  /**
   * Updates the DOM to reflect the current wishlist state.
   * Toggles CSS class and updates ARIA attributes for accessibility.
   * @returns {void}
   */
  async render() {
    const { isInWishlist } = this.state;
    const { labels, active } = this.element;
    this.classList.toggle('Wishlist--Active', isInWishlist);

    const source = isInWishlist ? active : labels;
    this.setAttribute('aria-label', source.aria);
    this.setAttribute('title', source.title);
  }

  notify({ action, title }) {
    const notification = document.querySelector('wishlist-notification');

    if (notification) {
      notification.show(action, title);
    }
  }

  removeScrollLock() {
    const template = document.querySelector('body');
    const page = document.querySelector('html');
    template.classList.remove('overflow-hidden');
    page.classList.remove('overflow-hidden');
  }

  /**
   * Lifecycle callback when element is added to the DOM.
   * Sets up event listeners and subscribes to Swym service events.
   * @returns {void}
   */
  connectedCallback() {
    this.addEventListener('click', this.handleClick);
    pubSub.subscribe('swym:service:update', this.handleServiceUpdate);

    const swymMounted = useSwym.listId;

    if (useSwym.windowAvailability && swymMounted) {
      this.handleServiceUpdate();
      return;
    }

    pubSub.subscribe('swym:service:ready', this.handleServiceUpdate);
  }

  /**
   * Lifecycle callback when an observed attribute changes.
   * Processes and stores attribute values, handles special cases for URLs and JSON.
   * @param {string} attribute - Name of the changed attribute
   * @param {string|null} previousValue - Previous attribute value
   * @param {string|null} value - New attribute value
   * @returns {void}
   */
  attributeChangedCallback(attribute, previousValue, value) {
    if (previousValue === value) return;

    if (attribute === 'du' && value) {
      const base = window?.Shopify ? `https://${window.Shopify.shop}` : '';
      value = `${base}${value}`;
    }

    if (attribute === 'cprops' && value) {
      value = JSON.parse(value);
    }

    if ((attribute === 'epi' || attribute === 'empi') && value) {
      value = parseInt(value);
    }

    this.product[attribute] = value;

    if (this.state.isInitialized) {
      setTimeout(() => this.updateWishlistStatus(), 0); //defers to the next tick
    }
  }
}

customElements.define('wishlist-btn', SwymButton);
